import React, { useState } from "react";
import { Chip, Popover, Box, Typography, Divider } from "@mui/material";
import { SwapHoriz } from "@mui/icons-material";

/**
 * Visual indicator rendered on timetable slots that have been swapped.
 *
 * Shows a small colored chip; on click opens a popover with swap details
 * (original teacher, new teacher, swap mode, request ID).
 *
 * Props:
 * - isSwapped: boolean — whether the slot has an active swap override
 * - swapRequestId: string|null — UUID of the swap request
 * - swapMode: "same_day"|"cross_day"|null — swap mode
 * - additionalSlot: object|null — cross-day gained slot details
 * - originalTeacher: string — name of the teacher originally assigned
 * - newTeacher: string — name of the teacher now teaching this slot
 */
const SwapBadge = ({
  isSwapped,
  swapRequestId,
  swapMode,
  additionalSlot,
  originalTeacher,
  newTeacher,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!isSwapped) return null;

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const modeLabel = swapMode === "cross_day" ? "Cross-Day" : "Same-Day";

  return (
    <>
      <Chip
        icon={<SwapHoriz sx={{ fontSize: 14 }} />}
        label="Swapped"
        size="small"
        color="info"
        variant="filled"
        onClick={handleClick}
        sx={{
          height: 20,
          fontSize: "0.65rem",
          cursor: "pointer",
          "& .MuiChip-icon": { fontSize: 14 },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 300 } } }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Swap Details
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2">
            <strong>Mode:</strong> {modeLabel}
          </Typography>
          {originalTeacher && (
            <Typography variant="body2">
              <strong>Original Teacher:</strong> {originalTeacher}
            </Typography>
          )}
          {newTeacher && (
            <Typography variant="body2">
              <strong>Current Teacher:</strong> {newTeacher}
            </Typography>
          )}
          {swapRequestId && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Request: {swapRequestId.slice(0, 8)}…
            </Typography>
          )}
        </Box>
        {additionalSlot && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
              Gained Slot (Other Day)
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Typography variant="caption">
                {additionalSlot.day} {additionalSlot.startTime} – {additionalSlot.endTime}
              </Typography>
              {additionalSlot.subjectName && (
                <Typography variant="caption">
                  {additionalSlot.subjectName}
                </Typography>
              )}
              {additionalSlot.className && (
                <Typography variant="caption" color="text.secondary">
                  {additionalSlot.className}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default SwapBadge;
